// SECTION CROSS SELL
export class SectionCrossSell extends HTMLElement {
  constructor() {
    super();
    this.isLoaded = false;
  }

  connectedCallback() {
    this._attachListeners();
  }

  get crossSellProduct() {
    return document.querySelector('cross-sell-product');
  }

  get formElement() {
    return this.querySelector('form');
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }

  _attachListeners() {
    this.loadRecommendations();
    this.crossSellProduct?.addToCartButton?.addEventListener('click', (event) => this._submitForm(event));
  }

  // Load the recommendations
  async loadRecommendations() {
    if (this.isLoaded) return;
  
    this.isLoaded = true;
  
    const intent = this.getAttribute('intent') || 'complementary';
    const url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute('product')}&limit=${this.getAttribute('limit') || 4}&section_id=${this.sectionId}&intent=${intent}`;
    
    const response = await fetch(url, { priority: intent === 'complementary' ? 'low' : 'auto' });
  
    if (!response.ok) {
      console.error('Failed to fetch recommendations');
      return;
    }
  
    const tempDiv = new DOMParser().parseFromString(await response.text(), 'text/html');
    const formElement = tempDiv.querySelector('form');
    
    if (formElement.length != 0) {
      this.innerHTML = formElement.outerHTML;
  
      this._attachListeners();
      this.closest('.shopify-section').classList.remove('hidden');
      
      this._initializeProductsState();
    } else {
      console.error('No recommendations found');
      this.closest('.shopify-section').remove();
    }
  }

  // Initialize products state on page load
  _initializeProductsState() {
    const crossSellProducts = this.formElement.querySelectorAll('cross-sell-product');
    let totalPrice = 0;

    crossSellProducts.forEach(product => {
      // Initialize quantity selectors to 1
      if (product.quantitySelector) {
        product.quantitySelector.quantity = 1;
        product.quantitySelector._toggleDisabled();
        product.quantitySelector._toggleQuantity();
      }

      if (product.productPrice) {
        totalPrice += product.productPrice;
      }
    });

    const addToCartButton = this.querySelector('.cross-sell__add-to-cart');
    const priceSpan = addToCartButton?.querySelector('.cross-sell__total-price');
    
    if (priceSpan && totalPrice > 0) {
      const currencySymbol = crossSellProducts[0]?.getAttribute('currency-symbol') || '';
      priceSpan.textContent = ` - ${totalPrice.toFixed(2)} ${currencySymbol}`;
      addToCartButton.disabled = false;
    }
  }

  // Submit the form
  async _submitForm(event) {
    event.preventDefault();

    this.crossSellProduct.addToCartButton.classList.add('is-loading');

    const formData = new FormData(this.formElement);
    const crossSellProducts = this.formElement.querySelectorAll('cross-sell-product');

    crossSellProducts.forEach((product, index) => {
      const checkbox = product.querySelector('.cross-sell__checkbox');
      const quantityInput = product.querySelector('.cross-sell__product-quantity');
      const variantIdInput = product.querySelector('input[name="item[id]"]');

      if (checkbox.checked) {
        const quantity = parseInt(quantityInput.value, 10);
        const variantId = variantIdInput.value;

        if (quantity > 0) {
          formData.append(`items[${index}][id]`, variantId);
          formData.append(`items[${index}][quantity]`, quantity);
        }
      }
    });

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (response.ok) {
        this.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: {} }));

        this._resetFormState(crossSellProducts);
        
        this.crossSellProduct.addToCartButton.classList.remove('is-loading');

      } else {
        console.error('Failed to add products to cart');
        this.crossSellProduct.addToCartButton.classList.remove('is-loading');
      }
    } catch (error) {
      console.error(error);
      this.crossSellProduct.addToCartButton.classList.remove('is-loading');
    }
  }
  
  _resetFormState(crossSellProducts) {
    this.formElement.reset();
    
    crossSellProducts.forEach(product => {
      const checkbox = product.querySelector('.cross-sell__checkbox');
      if (checkbox) checkbox.checked = false;
      
      // Reset quantity to 0
      if (product.quantitySelector) {
        product.quantitySelector.quantity = 0;
        product.inputValue.value = 0;
        product.quantitySelector._toggleDisabled();
        
        const quantityItem = product.parentElement.querySelector('.cross-sell__product-quantity');
        if (quantityItem) quantityItem.classList.remove('active');
      }
    });
    
    this.crossSellProduct._updateAddToCartButton();
  }
}


// CROSS SELL PRODUCT
export class CrossSellProduct extends HTMLElement {
  connectedCallback() {
    this.addToCartText = this.addToCartButton.textContent;

    this.decreaseButton.addEventListener('click', this._handleChange.bind(this));
    this.increaseButton.addEventListener('click', this._handleChange.bind(this));
    this.checkbox.addEventListener('click', this._handleChange.bind(this));

    // Set initial state - checkbox is already checked in HTML
    if (this.checkbox.checked && this.quantitySelector) {
      // Parent connects before its child selector is upgraded (tree order), so upgrade it first
      customElements.upgrade(this.quantitySelector);
      this.inputValue.value = 1;
      this.quantitySelector._toggleDisabled?.();
    }
  }

  get quantitySelector() {
    return this.querySelector('cross-sell-quantity-selector');
  }

  get checkbox() {
    return this.querySelector('input[type="checkbox"]');
  }

  get decreaseButton() {
    return this.querySelector('button[decrease]');
  }

  get increaseButton() {
    return this.querySelector('button[increase]');
  }

  get inputValue() {
    return this.querySelector('input[name="item[quantity]"]');
  }

  get productPrice() {
    return parseFloat(this.getAttribute('price').replace(',', '.'));
  }  

  get addToCartButton() {
    return document.querySelector('.cross-sell__add-to-cart');
  }

  get currencySymbol() {
    return this.getAttribute('currency-symbol');
  }

  // Handle the change event for the checkbox, increase and decrease buttons
  _handleChange(event) {
    if (event.target === this.checkbox) {
      if (this.checkbox.checked) {
        this.quantitySelector.quantity = 1;
        this.inputValue.value = 1;
        this._updateAddToCartButton();
      } else {
        this.quantitySelector.quantity = 0;
        this.inputValue.value = 0;
        this._updateAddToCartButton();
      }
      this.quantitySelector._toggleDisabled();
    }

    if (event.target === this.increaseButton) {
      this.quantitySelector.quantity++;
      if (this.quantitySelector.quantity > 0) {
        this.checkbox.checked = true;
      }
      this.inputValue.value = this.quantitySelector.quantity;
      this._updateAddToCartButton();
      this.quantitySelector._toggleDisabled();
    }

    if (event.target === this.decreaseButton) {
      if (this.quantitySelector.quantity > 0) {
        this.quantitySelector.quantity--;
      }
      if (this.quantitySelector.quantity === 0) {
        this.checkbox.checked = false;
      }
      this.inputValue.value = this.quantitySelector.quantity;
      this._updateAddToCartButton();
      this.quantitySelector._toggleDisabled();
    }
  }

  // Update the Add to Cart button with the total price
  _updateAddToCartButton() {
    let priceTotal = 0;
  
    const crossSellProducts = document.querySelectorAll('cross-sell-product');
    crossSellProducts.forEach((product) => {
      if (product.checkbox.checked) {
        priceTotal += product._multiplyPrice(product.productPrice, product.quantitySelector.quantity);
      }
    });
  
    const priceSpan = this.addToCartButton.querySelector('.cross-sell__total-price');
  
    if (priceTotal > 0) {
      priceSpan.textContent = ` - ${priceTotal.toFixed(2)} ${this.currencySymbol}`;
      this.addToCartButton.disabled = false;
    } else {
      priceSpan.textContent = "";
      this.addToCartButton.disabled = true;
    }
  }  

  // Multiply the price by the quantity, applying degressive discount if present
  _multiplyPrice(price, quantity) {
    var degressivePrice = this.querySelector('degressive-price');
    if (degressivePrice && degressivePrice.getDiscountedTotal) {
      return degressivePrice.getDiscountedTotal(parseInt(quantity));
    }
    return parseFloat(price) * parseInt(quantity);
  }

}



// QUANTITY SELECTOR
export class CrossSellQuantitySelector extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.inputElement = this.querySelector('input');
    this.quantityItem = this.parentElement.querySelector('.cross-sell__product-quantity');

    this.querySelector('button:first-of-type').addEventListener('click', () => this._toggleQuantity('decrease'), { signal: this._abortController.signal });
    this.querySelector('button:last-of-type').addEventListener('click', () => this._toggleQuantity('increase'), { signal: this._abortController.signal });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  // Get the cross-sell-product custom element for functions
  get crossSellProduct() {
    return document.querySelector('cross-sell-product');
  }

  get minValue() {
    return parseInt(this.inputElement.min);
  }

  get maxValue() {
    return parseInt(this.inputElement.max);
  }

  get quantity() {
    return parseInt(this.inputElement.value);
  }

  set quantity(quantity) {
    this.inputElement.value = quantity.toString();
  }

  // Increase or decrease the quantity
  _toggleQuantity() {
    this._toggleDisabled();
    if (this.quantity === 0) {
      this.quantityItem.classList.remove("active");
      this.crossSellProduct._updateAddToCartButton();
    } else {
      this.quantityItem.classList.add("active");
      this.crossSellProduct._updateAddToCartButton();
    }
  }

  // Toggle the disabled state of the decrease button
  _toggleDisabled() {
    Array.from(this.querySelectorAll('button')).forEach((item) => {
      item.disabled = (item === this.querySelector('button:first-of-type')) ? this.quantity === this.minValue : this.quantity === this.maxValue;
    });
  }
}


// Custom Elements declaration
if (!customElements.get('section-cross-sell')) {
  customElements.define('section-cross-sell', SectionCrossSell);
}

if (!customElements.get('cross-sell-product')) {
  customElements.define('cross-sell-product', CrossSellProduct);
}

if (!customElements.get('cross-sell-quantity-selector')) {
  customElements.define('cross-sell-quantity-selector', CrossSellQuantitySelector);
}